'use client';

import { FC, useMemo } from 'react';

import { hasPermission } from '@ravago/shared/next-data-access';
import { FeatureToggle } from '@ravago/shared/page-data/models/config';
import { InvoiceForCurrencyForLegalEntity } from '@ravago/shared/page-data/models/elements';
import { RendererGlobals } from '@ravago/shared/page-data/models/utils';

import { getLanguageCode } from '../../../../utils/locale/locale.utils';
import { useInvoiceStateContext } from '../context/invoice-context';
import { useFetchClosedInvoices } from '../hooks/fetch-closed-invoices.hook';
import { useFetchDocument } from '../hooks/fetch-document.hook';
import { useFilterOpenInvoices } from '../hooks/filter-open-invoices.hook';
import { BillingType } from '../models/billing-type.model';
import { Toggle } from '../models/feature-permissions.model';
import { InvoiceProviders } from '../providers/invoice-providers';

import { InvoiceChipList } from './invoice-chip-list/invoice-chip-list';
import { InvoiceOverview } from './invoice-overview/invoice-overview';

type Props = {
   globals: RendererGlobals;
   featureToggles: FeatureToggle[];
   openInvoices: InvoiceForCurrencyForLegalEntity[];
};

export const Invoices: FC<Props> = ({ globals, featureToggles, openInvoices }) => {
   const { actions, locale } = globals;
   const { downloadInvoiceDocument } = actions;
   const { documentToDownload, selectedBillingType, closedInvoicesQueryParams } = useInvoiceStateContext();

   const language = useMemo(() => getLanguageCode(locale), [locale]);
   const isOpenBillingType = selectedBillingType === BillingType.OPEN;
   const hasClosedInvoicePermissions = hasPermission(featureToggles, Toggle.CLOSED_INVOICES);

   useFetchClosedInvoices(hasClosedInvoicePermissions, isOpenBillingType, closedInvoicesQueryParams);
   useFetchDocument(downloadInvoiceDocument, documentToDownload, language);
   useFilterOpenInvoices(isOpenBillingType, openInvoices);

   return (
      <>
         <InvoiceChipList isOpenSelected={isOpenBillingType} hasClosedInvoicePermissions={hasClosedInvoicePermissions} />
         <InvoiceOverview showOpenInvoices={isOpenBillingType} featureToggles={featureToggles} />
      </>
   );
};

export const InvoicesWithContext = ({ globals, featureToggles, openInvoices }: Props) => (
   <InvoiceProviders>
      <Invoices globals={globals} featureToggles={featureToggles} openInvoices={openInvoices} />
   </InvoiceProviders>
);
